import styles from './BottomBar.module.scss';
import Button from '../Button/ButtonComponent';
import { ReactSVG } from 'react-svg';
import { useRef, useState, useEffect } from 'react';
import { dataResources } from '@portal-internet/core';
import BottomBarCancel from '../BottomBarCancel';
import SaveLayoutComponent from '../SaveLayout';
import { useRedl } from 'hooks/useRedl';
import Flex from '../Flex/FlexComponent';
import BubbleMsg from '../BubbleMsg/BubbleMsg';
import { useRedlLayout } from 'hooks/useRedlLayout';
import { getZonaEditable } from 'components/REDL/utils/getZonaEditable';

const { basePath } = dataResources;

const _getMaxItems = (list = []) => {
    if (list.length === 0) return list;
    const mapa = new Map();
    list.forEach(line => {
        if (line) {
            const [dataSource, matchStr] = line.split("|");
            const match = Number(matchStr);

            if (!mapa.has(dataSource) || match > mapa.get(dataSource)) {
                mapa.set(dataSource, matchStr);
            }
        }
    });
    return Array.from(mapa.entries()).map(
        ([dataSource , matches]) => `${dataSource}: ${matches}`
    );
};

const _calculateRedlItemsDataSource = (structure, setRedlItemsDataSource) => {
    const items = [];
    const zonaEditable = getZonaEditable(structure);
    if (Array.isArray(zonaEditable?.children)) {
        zonaEditable.children.forEach((component, key ) => {
            const aux = _collectItemsDataSource(component, key + 1)
            if (aux.length > 0) items.push(...aux);
        });
    }
    setRedlItemsDataSource(_getMaxItems(items));
};

const _collectItemsDataSource = (structure) => {
    if (!structure) return [];
    const { finalProps, children } = structure;
    if (finalProps?.redlAuxInfo)  return [finalProps.redlAuxInfo];

    return Array.isArray(children)
        ? children.flatMap(_collectItemsDataSource)
        : [];
};

export default function BottomBarComponent() {
    const { setShowLayout, setZoom, hidePreview, setHidePreview, redlMetadataStructure } = useRedl();
    const ref = useRef(null);
    const [isMoveApplied, setIsMoveApplied] = useState(false);
    const [showMsg, setShowMsg] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [redlItemsDataSource, setRedlItemsDataSource] = useState([]);
    const { layoutName } = useRedlLayout();

    const onZoomClick = (up) => () => {
        setZoom(prevState => up ? prevState + 5 : Math.max(5, prevState - 5));
    };

    const onHideClick = () => {
        setZoom(100);
        setHidePreview(prevState => !prevState);
    };

    const onOpenUserManualURL = () => window.open('https://confluence.ccma.cat/x/9gGFGw', '_blank')
    
    const onInfoClick = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        setShowMsg(!showMsg);
    }

    useEffect(() => {
        if(redlMetadataStructure?.length) _calculateRedlItemsDataSource(redlMetadataStructure, setRedlItemsDataSource);
    }, [redlMetadataStructure]);

    return (
        <div className={styles.redlBottomBar} ref={ref} data-testid="REDL-bottom-bar">
            <div className={styles.redlBottomBarInner}>
                <div className={styles.redlBottomBarInnerLeft}>

                    <ul className={styles.redlBottomBarTools}>
                        <li className={styles.redlBottomBarToolsItem}>
                            <Button variant="tertiary" size="sm" onClick={() => setShowLayout(prevState => !prevState)} title="Mostra/Oculta la barra lateral"
                                disabled={hidePreview}>
                                <ReactSVG
                                    src={basePath + '/_resources/REDL/icons/layout-sidebar-reverse-square.svg?v=JIT_ENV_RESOURCES_VERSION'} />
                            </Button>
                        </li>
                        <li className={styles.redlBottomBarToolsItem}>
                            <Button variant="tertiary" size="sm" title="Amplia Zoom"
                                onClick={onZoomClick(true)} >
                                <ReactSVG src={basePath + '/_resources/REDL/icons/zoom-in.svg?v=JIT_ENV_RESOURCES_VERSION'} />
                            </Button>
                        </li>
                        <li className={styles.redlBottomBarToolsItem}>
                            <Button variant="tertiary" size="sm" title="Redueix zoom"
                                onClick={onZoomClick(false)} >
                                <ReactSVG src={basePath + '/_resources/REDL/icons/zoom-out.svg?v=JIT_ENV_RESOURCES_VERSION'} />
                            </Button>
                        </li>
                        <li className={styles.redlBottomBarToolsItem}>
                            <Button variant="tertiary" size="sm" title="Mostra REDL"
                                onClick={onHideClick}>
                                {
                                    hidePreview ?
                                        <ReactSVG
                                            src={basePath + '/_resources/REDL/icons/eye-slash.svg?v=JIT_ENV_RESOURCES_VERSION'} />
                                        :
                                        <ReactSVG
                                            src={basePath + '/_resources/REDL/icons/eye-fill.svg?v=JIT_ENV_RESOURCES_VERSION'} />
                                }
                            </Button>
                        </li>
                        <li className={styles.redlBottomBarToolsItem}>
                            <BubbleMsg 
                                showMsg={showMsg} 
                                setShowMsg={setShowMsg} 
                                type = "list"
                                content = {redlItemsDataSource}
                            />
                            <Button variant="tertiary" size="sm" title="Mostra informació de la pàgina" data-testid="REDL-bottom-btn-information"
                                onClick={(ev) => { onInfoClick(ev) }}>
                                {
                                    showMsg ?
                                        <ReactSVG
                                            src={basePath + '/_resources/REDL/icons/info-circle-fill.svg?v=JIT_ENV_RESOURCES_VERSION'} />
                                        :
                                        <ReactSVG
                                            src={basePath + '/_resources/REDL/icons/info-circle.svg?v=JIT_ENV_RESOURCES_VERSION'} />
                                }
                            </Button>
                        </li>
                        <li className={styles.redlBottomBarToolsItem}>
                            <Button variant="tertiary" size="sm" title="Ajuda"
                                onClick={onOpenUserManualURL}>
                                <ReactSVG src={basePath + '/_resources/REDL/icons/question-circle.svg?v=JIT_ENV_RESOURCES_VERSION'} />
                            </Button>
                        </li>
                    </ul>
                </div>
                {layoutName && (
                    <div className={styles.redlBottomBarInnerCenter}>
                        <span id="layout-label" className={styles.layoutLabel}>
                            Layout:
                        </span>
                        <h1 aria-describedby="layout-label">
                            {layoutName}
                        </h1>
                    </div>
                )}
                <div className={styles.redlBottomBarInnerRight}>
                    <ul className={styles.redlBottomBarActions}>
                        <li className={styles.redlBottomBarActionsItem}>
                            <BottomBarCancel />
                        </li>
                        <li className={styles.redlBottomBarActionsItem}>
                            <SaveLayoutComponent />
                        </li>
                    </ul>
                </div>
            </div>
        </div >
    );
}
